import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Search,
  ChevronDown,
  ChevronUp,
  Edit,
  Save,
  X,
  Filter,
  Calendar,
  Building2,
  FileText,
  ArrowLeft,
  ArrowRight,
  Check,
  AlertCircle,
  Eye,
  Download,
  Trash2,
  CheckSquare,
  Archive
} from 'lucide-react';
import toast from 'react-hot-toast';
import FileViewerModal from '../components/FileViewerModal';
import { formatCurrency as sharedFormatCurrency } from '../utils/currency';
import SearchableSelect from '../components/SearchableSelect';
import MappedTypeSelect from '../components/FileTracking/MappedTypeSelect';
import {
  getImports,
  getHotelRevenue,
  getHotelRoomStats,
  createHotelRevenue,
  createHotelRoomStats,
  updateHotelRevenue,
  updateHotelRoomStats,
  deleteHotelRevenue,
  deleteHotelRoomStats,
  fetchImportPdf,
  fetchImportPdfBulk,
  getRevenueTypes,
  getRoomStatTypes,
  deleteImport,
  getEtlConfigsForHotels,
  clearManualReviewFlag
} from '../utils/api';
import { detectFileViewerType, getFileNameFromPath } from '../utils/fileViewer';
import { getLocationsByOrgId } from '../utils/locationApi';
import { getOrganizations } from '../utils/organizationApi';
import { moveImportBusinessDate, refreshImportDashboard } from '../utils/importCorrectionsApi';
import BusinessDateSheet from './importReview/BusinessDateSheet';
import { describeDates, distinctDates, toDateOnly } from './importReview/businessDate';
import './importReview/importReview.css';

/**
 * Import Review — compare a source file against what we imported from it.
 *
 * Rendered as a tab of ETL Configuration, which owns the page title, the
 * organization scope and the page frame; `embedded` suppresses this file's own
 * copies of all three. The standalone route still works for deep links and
 * passes no props.
 */
const ImportReviewPage = ({ embedded = false } = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // State for filters and data
  const [organizations, setOrganizations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [imports, setImports] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [roomStatsData, setRoomStatsData] = useState([]);
  const [revenueTypes, setRevenueTypes] = useState([]);
  const [roomStatTypes, setRoomStatTypes] = useState([]);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [selectedImportId, setSelectedImportId] = useState('');
  const [currentImportIndex, setCurrentImportIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('revenue'); // 'revenue' or 'roomStats'
  const [dateSheetOpen, setDateSheetOpen] = useState(false);
  const [correctionNote, setCorrectionNote] = useState(''); // reason logged with the next saved correction
  
  // State for loading and UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    filters: true,
    revenue: true,
    roomStats: true
  });
  
  // State for editing
  const [editingRevenue, setEditingRevenue] = useState(null);
  const [editingRoomStats, setEditingRoomStats] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // State for adding new revenue row
  const [isAddingRevenue, setIsAddingRevenue] = useState(false);
  const [newRevenue, setNewRevenue] = useState(null);

  // State for adding new room stat row
  const [isAddingRoomStat, setIsAddingRoomStat] = useState(false);
  const [newRoomStat, setNewRoomStat] = useState(null);
  
  // State for PDF viewing
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  // State for file viewer modal
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileType, setFileType] = useState(null); // 'pdf', 'text', or 'excel'
  const [fileName, setFileName] = useState(null);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [fileError, setFileError] = useState(null);

  // State for import deletion
  const [isDeletingImport, setIsDeletingImport] = useState(false);

  // State for manual review flag
  const [hasManualReviewFlag, setHasManualReviewFlag] = useState(false);
  const [isClearingReview, setIsClearingReview] = useState(false);
  const [reviewFileType, setReviewFileType] = useState(null);

  // State for bulk download
  const [bulkDownloadStartDate, setBulkDownloadStartDate] = useState('');
  const [bulkDownloadEndDate, setBulkDownloadEndDate] = useState('');
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);

  // Track whether we've initialized to prevent re-running
  const hasInitialized = useRef(false);

  // Update URL params without triggering re-initialization
  const updateUrlParams = useCallback((params) => {
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  // Initialize from URL params or localStorage - runs ONCE on mount
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const initializeData = async () => {
      try {
        // Load organizations
        const orgs = await getOrganizations();
        setOrganizations(orgs);

        // Get organization from URL or localStorage
        const urlOrgId = searchParams.get('orgId');
        const storedOrgId = localStorage.getItem('currentOrgId');
        const orgId = urlOrgId || storedOrgId;

        if (orgId && orgs.length > 0) {
          const org = orgs.find(o => o.id.toString() === orgId);
          if (org) {
            setCurrentOrganization(org);
            await loadLocations(orgId);

            // Load location and import from URL params
            const locationId = searchParams.get('locationId');
            const importId = searchParams.get('importId');

            if (locationId) {
              setSelectedLocationId(locationId);

              // Always load the imports list first
              const allImports = await getImports({ orgId, locationId });
              setImports(allImports);

              if (allImports.length > 0) {
                // Use import from URL, or default to most recent
                const targetId = importId || allImports[0].id.toString();
                const targetIndex = allImports.findIndex(imp => imp.id.toString() === targetId);

                setSelectedImportId(targetId);
                setCurrentImportIndex(targetIndex >= 0 ? targetIndex : 0);
                await loadImportData(orgId, locationId, targetId);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error initializing import review page:', error);
        setError('Failed to load initial data');
      }
    };

    initializeData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLocations = async (orgId) => {
    try {
      const locations = await getLocationsByOrgId(orgId);
      setLocations(locations);
    } catch (error) {
      console.error('Error loading locations:', error);
      toast.error('Failed to load locations');
    }
  };

  const loadImports = async (orgId, locationId, autoSelect = true) => {
    try {
      setLoading(true);
      const allImports = await getImports({ orgId, locationId });
      setImports(allImports);

      if (allImports.length > 0 && autoSelect) {
        const mostRecent = allImports[0]; // Already sorted by date DESC
        setSelectedImportId(mostRecent.id);
        setCurrentImportIndex(0);
        await loadImportData(orgId, locationId, mostRecent.id);
      }
    } catch (error) {
      console.error('Error loading imports:', error);
      setError('Failed to load imports');
    } finally {
      setLoading(false);
    }
  };

  const loadImportData = async (orgId, locationId, importId) => {
    try {
      setLoading(true);
      setError('');

      // Load both revenue and room stats data for this import, plus revenue types and room stat types
      // IMPORTANT: Revenue types and room stat types are filtered by locationId
      const [revenue, roomStats, revTypes, roomTypes] = await Promise.all([
        getHotelRevenue({ orgId, locationId, importId }),
        getHotelRoomStats({ orgId, locationId, importId }),
        getRevenueTypes(orgId, locationId),
        // includeMappings — the Maps To column edits roomStatMappedTypes rows.
        getRoomStatTypes(orgId, locationId, false, true)
      ]);

      setRevenueData(revenue);
      setRoomStatsData(roomStats);
      setRevenueTypes(revTypes);
      setRoomStatTypes(roomTypes);

      // Update URL params (replace to avoid history bloat, won't re-trigger init)
      updateUrlParams({
        orgId,
        locationId,
        importId,
        tab: activeTab
      });

    } catch (error) {
      console.error('Error loading import data:', error);
      setError('Failed to load import data');
    } finally {
      setLoading(false);
    }
  };

  // Check if this location has any manual review flag
  useEffect(() => {
    const checkReviewFlag = async () => {
      if (selectedLocationId) {
        try {
          const configs = await getEtlConfigsForHotels([selectedLocationId]);
          if (configs && configs.length > 0) {
            const config = configs[0];
            const fileConfigs = config.fileConfigs || [];
            // Check all file configs for any manual review flag for this location
            // First try to match by importId, then fall back to any review flag
            let matchingConfig = fileConfigs.find(fc =>
              fc.tracking?.manualReviewRequired &&
              !fc.tracking?.manualReviewCleared &&
              String(fc.tracking?.manualReviewImportId) === String(selectedImportId)
            );
            // If no exact match, check for any pending review for this location
            if (!matchingConfig) {
              matchingConfig = fileConfigs.find(fc =>
                fc.tracking?.manualReviewRequired &&
                !fc.tracking?.manualReviewCleared
              );
            }
            setHasManualReviewFlag(!!matchingConfig);
            setReviewFileType(matchingConfig?.fileType || null);
          } else {
            setHasManualReviewFlag(false);
            setReviewFileType(null);
          }
        } catch (error) {
          console.error('Error checking review flag:', error);
          setHasManualReviewFlag(false);
          setReviewFileType(null);
        }
      } else {
        setHasManualReviewFlag(false);
        setReviewFileType(null);
      }
    };
    checkReviewFlag();
  }, [selectedLocationId, selectedImportId]);

  // Handle clearing the manual review flag
  const handleClearReviewFlag = async () => {
    if (!selectedLocationId || !reviewFileType) return;

    setIsClearingReview(true);
    try {
      await clearManualReviewFlag(selectedLocationId, reviewFileType, 'user');
      setHasManualReviewFlag(false);
      setReviewFileType(null);
      toast.success('Manual review flag cleared');
    } catch (error) {
      console.error('Error clearing review flag:', error);
      toast.error('Failed to clear review flag');
    } finally {
      setIsClearingReview(false);
    }
  };

  const handleOrganizationChange = async (orgId) => {
    const org = organizations.find(o => o.id.toString() === orgId);
    setCurrentOrganization(org);
    setSelectedLocationId('');
    setSelectedImportId('');
    setImports([]);
    setRevenueData([]);
    setRoomStatsData([]);
    
    if (orgId) {
      localStorage.setItem('currentOrgId', orgId);
      await loadLocations(orgId);
    }
  };

  const handleLocationChange = async (locationId) => {
    setSelectedLocationId(locationId);
    setSelectedImportId('');
    setImports([]);
    setRevenueData([]);
    setRoomStatsData([]);
    
    if (locationId && currentOrganization) {
      await loadImports(currentOrganization.id, locationId);
    }
  };

  const handleImportChange = async (importId) => {
    const importIndex = imports.findIndex(imp => imp.id.toString() === importId);
    setSelectedImportId(importId);
    setCurrentImportIndex(importIndex);
    
    if (importId && currentOrganization && selectedLocationId) {
      await loadImportData(currentOrganization.id, selectedLocationId, importId);
    }
  };

  const navigateImports = async (direction) => {
    const newIndex = direction === 'next' ? currentImportIndex + 1 : currentImportIndex - 1;
    
    if (newIndex >= 0 && newIndex < imports.length) {
      const newImport = imports[newIndex];
      setCurrentImportIndex(newIndex);
      setSelectedImportId(newImport.id);
      
      if (currentOrganization && selectedLocationId) {
        await loadImportData(currentOrganization.id, selectedLocationId, newImport.id);
      }
    }
  };

  // After a row is corrected, added or deleted: refresh the dashboard for the
  // affected dates and log the optional reason on the file's history.
  const recordCorrection = async (...dates) => {
    const refreshDates = [...new Set(dates.map(toDateOnly).filter(Boolean))];
    if (!selectedImportId || !refreshDates.length) return;
    const note = correctionNote.trim();
    try {
      const result = await refreshImportDashboard(selectedImportId, { dates: refreshDates, note: note || undefined });
      setCorrectionNote('');
      if ((result.dashboardRefresh || []).some((r) => !r.ok)) {
        toast.error('Saved, but the dashboard could not be refreshed for every date.');
      }
    } catch (err) {
      console.error('Dashboard refresh after correction failed:', err);
      toast.error('Saved, but the dashboard could not be refreshed.');
    }
  };

  const handleEditRevenue = (revenue) => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to edit a different record?')) {
        return;
      }
    }
    setEditingRevenue({ ...revenue });
    setEditingRoomStats(null);
    setHasUnsavedChanges(false);
  };

  const handleEditRoomStats = (roomStat) => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to edit a different record?')) {
        return;
      }
    }
    setEditingRoomStats({ ...roomStat });
    setEditingRevenue(null);
    setHasUnsavedChanges(false);
  };

  const handleSaveRevenue = async () => {
    if (!editingRevenue) return;
    
    try {
      const before = revenueData.find(item => item.id === editingRevenue.id);
      const updatedRevenue = await updateHotelRevenue(editingRevenue.id, editingRevenue);
      setRevenueData(prev => prev.map(item => 
        item.id === editingRevenue.id ? updatedRevenue : item
      ));
      setEditingRevenue(null);
      setHasUnsavedChanges(false);
      toast.success('Revenue record updated successfully');
      recordCorrection(before?.effectiveDate, updatedRevenue?.effectiveDate || editingRevenue.effectiveDate);
    } catch (error) {
      console.error('Error updating revenue:', error);
      toast.error('Failed to update revenue record');
    }
  };

  const handleSaveRoomStats = async () => {
    if (!editingRoomStats) return;
    
    try {
      const before = roomStatsData.find(item => item.id === editingRoomStats.id);
      const updatedRoomStats = await updateHotelRoomStats(editingRoomStats.id, editingRoomStats);
      setRoomStatsData(prev => prev.map(item => 
        item.id === editingRoomStats.id ? updatedRoomStats : item
      ));
      setEditingRoomStats(null);
      setHasUnsavedChanges(false);
      toast.success('Room stats record updated successfully');
      recordCorrection(before?.effectiveDate, updatedRoomStats?.effectiveDate || editingRoomStats.effectiveDate);
    } catch (error) {
      console.error('Error updating room stats:', error);
      toast.error('Failed to update room stats record');
    }
  };

  const handleCancelEdit = () => {
    if (hasUnsavedChanges && !window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
      return;
    }
    setEditingRevenue(null);
    setEditingRoomStats(null);
    setHasUnsavedChanges(false);
  };

  const handleDeleteRevenue = async (id) => {
    if (!window.confirm('Are you sure you want to delete this revenue record?')) {
      return;
    }
    
    try {
      const removed = revenueData.find(item => item.id === id);
      await deleteHotelRevenue(id);
      setRevenueData(prev => prev.filter(item => item.id !== id));
      toast.success('Revenue record deleted successfully');
      recordCorrection(removed?.effectiveDate);
    } catch (error) {
      console.error('Error deleting revenue:', error);
      toast.error('Failed to delete revenue record');
    }
  };

  const handleDeleteRoomStats = async (id) => {
    if (!window.confirm('Are you sure you want to delete this room stats record?')) {
      return;
    }

    try {
      const removed = roomStatsData.find(item => item.id === id);
      await deleteHotelRoomStats(id);
      setRoomStatsData(prev => prev.filter(item => item.id !== id));
      toast.success('Room stats record deleted successfully');
      recordCorrection(removed?.effectiveDate);
    } catch (error) {
      console.error('Error deleting room stats:', error);
      toast.error('Failed to delete room stats record');
    }
  };

  const handleAddRevenue = () => {
    if (hasUnsavedChanges && !window.confirm('You have unsaved changes. Are you sure you want to add a new record?')) {
      return;
    }

    // Get the most recent date from existing revenue data or use today
    let formattedDate;
    if (revenueData.length > 0) {
      // Extract date part from the first revenue record
      let dateOnly = revenueData[0].effectiveDate;
      if (dateOnly.includes(' ')) {
        dateOnly = dateOnly.split(' ')[0];
      } else if (dateOnly.includes('T')) {
        dateOnly = dateOnly.split('T')[0];
      }
      formattedDate = dateOnly; // Already in YYYY-MM-DD format
    } else {
      // Use today's date in local timezone
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      formattedDate = `${year}-${month}-${day}`;
    }

    setNewRevenue({
      effectiveDate: formattedDate,
      revenueTypeId: null,
      currentValue: 0,
      orgId: currentOrganization?.id,
      locationId: parseInt(selectedLocationId),
      importId: parseInt(selectedImportId)
    });
    setIsAddingRevenue(true);
    setEditingRevenue(null);
    setEditingRoomStats(null);
    setHasUnsavedChanges(false);
  };

  const handleSaveNewRevenue = async () => {
    if (!newRevenue) return;

    try {
      const createdRevenue = await createHotelRevenue(newRevenue);
      setRevenueData(prev => [createdRevenue, ...prev]);
      setNewRevenue(null);
      setIsAddingRevenue(false);
      toast.success('Revenue record created successfully');
      recordCorrection(createdRevenue?.effectiveDate || newRevenue.effectiveDate);
    } catch (error) {
      console.error('Error creating revenue:', error);
      toast.error('Failed to create revenue record');
    }
  };

  const handleCancelAddRevenue = () => {
    setNewRevenue(null);
    setIsAddingRevenue(false);
  };

  const updateNewRevenueField = (field, value) => {
    setNewRevenue(prev => ({ ...prev, [field]: value }));
  };

  const handleAddRoomStat = () => {
    if (hasUnsavedChanges && !window.confirm('You have unsaved changes. Are you sure you want to add a new record?')) {
      return;
    }

    // Get the most recent date from existing room stats data or use today
    let formattedDate;
    if (roomStatsData.length > 0) {
      // Extract date part from the first room stat record
      let dateOnly = roomStatsData[0].effectiveDate;
      if (dateOnly.includes(' ')) {
        dateOnly = dateOnly.split(' ')[0];
      } else if (dateOnly.includes('T')) {
        dateOnly = dateOnly.split('T')[0];
      }
      formattedDate = dateOnly;
    } else {
      // Use today's date in local timezone
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      formattedDate = `${year}-${month}-${day}`;
    }

    setNewRoomStat({
      effectiveDate: formattedDate,
      roomStatTypeId: null,
      currentValue: 0,
      mtd: null,
      ytd: null,
      lytd: null,
      lymtd: null,
      ly: null,
      orgId: currentOrganization?.id,
      locationId: parseInt(selectedLocationId),
      importId: parseInt(selectedImportId)
    });
    setIsAddingRoomStat(true);
    setEditingRevenue(null);
    setEditingRoomStats(null);
    setHasUnsavedChanges(false);
  };

  const handleSaveNewRoomStat = async () => {
    if (!newRoomStat) return;

    if (!newRoomStat.roomStatTypeId) {
      toast.error('Please select a room stat type');
      return;
    }

    try {
      const createdRoomStat = await createHotelRoomStats(newRoomStat);
      setRoomStatsData(prev => [createdRoomStat, ...prev]);
      setNewRoomStat(null);
      setIsAddingRoomStat(false);
      toast.success('Room stat record created successfully');
      recordCorrection(createdRoomStat?.effectiveDate || newRoomStat.effectiveDate);
    } catch (error) {
      console.error('Error creating room stat:', error);
      toast.error('Failed to create room stat record');
    }
  };

  const handleCancelAddRoomStat = () => {
    setNewRoomStat(null);
    setIsAddingRoomStat(false);
  };

  const updateNewRoomStatField = (field, value) => {
    setNewRoomStat(prev => ({ ...prev, [field]: value }));
  };

  const updateEditingField = (field, value, isRoomStats = false) => {
    setHasUnsavedChanges(true);
    if (isRoomStats) {
      setEditingRoomStats(prev => ({ ...prev, [field]: value }));
    } else {
      setEditingRevenue(prev => ({ ...prev, [field]: value }));
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleViewFile = async () => {
    if (!selectedImport || !selectedImport.nightAuditPath) {
      toast.error('No file path available for this import.');
      return;
    }

    setIsLoadingFile(true);
    setFileError(null);
    setIsFileViewerOpen(true);

    try {
      const path = selectedImport.nightAuditPath;
      const type = detectFileViewerType(path, 'pdf');
      
      setFileType(type);
      setFileName(getFileNameFromPath(path));

      const blobUrl = await fetchImportPdf(path);
      setFileUrl(blobUrl);
    } catch (error) {
      console.error('Failed to load file:', error);
      setFileError(error.message || 'An unknown error occurred.');
      // Keep the modal open to show the error message from the modal itself
    } finally {
      setIsLoadingFile(false);
    }
  };

  const handleDownloadFile = async () => {
    if (!selectedImport || !selectedImport.nightAuditPath) {
      toast.error('No file path available for this import.');
      return;
    }
    try {
      const url = await fetchImportPdf(selectedImport.nightAuditPath);
      const link = document.createElement('a');
      link.href = url;
      const fileName = selectedImport.nightAuditPath.split('/').pop();
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(`Failed to download file: ${error.message}`);
    }
  };

  // Helper to extract date-only string for comparison
  const getDateOnly = (dateString) => {
    if (!dateString) return '';
    if (dateString.includes(' ')) return dateString.split(' ')[0];
    if (dateString.includes('T')) return dateString.split('T')[0];
    return dateString;
  };

  // Get imports matching the bulk download date range
  const getBulkDownloadMatches = () => {
    if (!bulkDownloadStartDate || !bulkDownloadEndDate) return [];
    return imports.filter(imp => {
      if (!imp.nightAuditPath) return false;
      const d = getDateOnly(imp.effectiveDate || imp.createdAt);
      return d >= bulkDownloadStartDate && d <= bulkDownloadEndDate;
    });
  };

  const handleBulkDownloadPdfs = async () => {
    const matches = getBulkDownloadMatches();
    if (matches.length === 0) {
      toast.error('No imports with files found in the selected date range.');
      return;
    }

    setIsBulkDownloading(true);
    try {
      const files = matches.map(imp => {
        const d = getDateOnly(imp.effectiveDate || imp.createdAt);
        const originalName = imp.nightAuditPath.split('/').pop().split('?')[0];
        return {
          url: imp.nightAuditPath,
          filename: `${d}_${originalName}`
        };
      });

      const result = await fetchImportPdfBulk(files);

      // Trigger browser download
      const url = URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = url;
      const locationName = selectedLocation?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'imports';
      link.download = `${locationName}_${bulkDownloadStartDate}_to_${bulkDownloadEndDate}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (result.failed > 0) {
        toast.success(`Downloaded ${result.succeeded} files. ${result.failed} files failed.`);
      } else {
        toast.success(`Downloaded ${result.succeeded} files successfully.`);
      }
    } catch (error) {
      toast.error(`Bulk download failed: ${error.message}`);
    } finally {
      setIsBulkDownloading(false);
    }
  };

  const closeFileViewer = () => {
    setIsFileViewerOpen(false);
    setFileUrl(null);
    setFileType(null);
    setFileName(null);
    if (fileUrl) {
      URL.revokeObjectURL(fileUrl);
    }
  };

  const handleDeleteImport = async () => {
    if (!selectedImport) return;
    
    const confirmMessage = `Are you sure you want to delete this import and ALL associated data?\n\n` +
      `Import: #${selectedImport.id}\n` +
      `Date: ${formatDate(selectedImport.effectiveDate || selectedImport.createdAt)}\n` +
      `Revenue Records: ${revenueData.length}\n` +
      `Room Stats Records: ${roomStatsData.length}\n\n` +
      `This action cannot be undone!`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      setIsDeletingImport(true);
      await deleteImport(selectedImport.id);
      
      toast.success('Import and associated data deleted successfully');
      
      // Navigate to a different import or clear selection
      const remainingImports = imports.filter(imp => imp.id !== selectedImport.id);
      
      if (remainingImports.length > 0) {
        // Navigate to the next available import
        const nextImport = remainingImports[0];
        setSelectedImportId(nextImport.id);
        setCurrentImportIndex(0);
        
        if (currentOrganization && selectedLocationId) {
          await loadImportData(currentOrganization.id, selectedLocationId, nextImport.id);
        }
      } else {
        // No more imports, clear everything
        setSelectedImportId('');
        setRevenueData([]);
        setRoomStatsData([]);
        setImports([]);
        
        // Reload imports to refresh the list
        if (currentOrganization && selectedLocationId) {
          await loadImports(currentOrganization.id, selectedLocationId);
        }
      }
    } catch (error) {
      console.error('Error deleting import:', error);
      toast.error('Failed to delete import: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsDeletingImport(false);
    }
  };

  const formatCurrency = (value, currency) => {
    if (value === null || value === undefined) return '';
    return sharedFormatCurrency(value, currency);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // Handle SQL Server datetime format (2025-08-04 00:00:00.0000000)
    let dateOnly;
    if (dateString.includes(' ')) {
      // Extract just the date part from SQL Server datetime
      dateOnly = dateString.split(' ')[0];
    } else if (dateString.includes('T')) {
      // ISO format, extract date part
      dateOnly = dateString.split('T')[0];
    } else {
      // Already just a date
      dateOnly = dateString;
    }
    
    // Parse as local date to avoid timezone shifts
    const dateParts = dateOnly.split('-');
    if (dateParts.length !== 3) {
      return 'Invalid Date';
    }
    
    const year = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
    const day = parseInt(dateParts[2]);
    
    const date = new Date(year, month, day);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    return date.toLocaleDateString();
  };

  // Helper function to format date for input fields without timezone issues
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    // Handle SQL Server datetime format (2025-08-04 00:00:00.0000000)
    let dateOnly;
    if (dateString.includes(' ')) {
      // Extract just the date part from SQL Server datetime
      dateOnly = dateString.split(' ')[0];
    } else if (dateString.includes('T')) {
      // ISO format, extract date part
      dateOnly = dateString.split('T')[0];
    } else {
      // Already just a date
      dateOnly = dateString;
    }
    
    // Parse as local date to avoid timezone shifts
    const dateParts = dateOnly.split('-');
    if (dateParts.length !== 3) {
      return '';
    }
    
    const year = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
    const day = parseInt(dateParts[2]);
    
    const date = new Date(year, month, day);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '';
    }
    
    // Format for input field
    const adjustedYear = date.getFullYear();
    const adjustedMonth = String(date.getMonth() + 1).padStart(2, '0');
    const adjustedDay = String(date.getDate()).padStart(2, '0');
    return `${adjustedYear}-${adjustedMonth}-${adjustedDay}`;
  };

  // Helper function to get revenue type name by ID
  const getRevenueTypeName = (typeId) => {
    if (!typeId || !revenueTypes.length) return typeId || 'N/A';
    const revenueType = revenueTypes.find(type => type.id === typeId);
    return revenueType ? revenueType.name || revenueType.accountName : typeId;
  };

  // Helper function to get room stat type name by ID
  const getRoomStatTypeName = (typeId) => {
    if (!typeId || !roomStatTypes.length) return typeId || 'N/A';
    const roomStatType = roomStatTypes.find(type => type.id === typeId);
    return roomStatType ? roomStatType.name : typeId;
  };

  // Move every row of the import (and the file record) to a new business date
  // in one server-side transaction; the server refreshes the dashboard too.
  const handleMoveBusinessDate = async (body) => {
    const result = await moveImportBusinessDate(selectedImportId, body);
    const moved = (result.moved?.revenueRows || 0) + (result.moved?.roomStatRows || 0);
    const failedRefresh = (result.dashboardRefresh || []).filter((r) => !r.ok).length;
    toast.success(`Moved ${moved} row${moved === 1 ? '' : 's'} to ${describeDates(result.toDates || [])}`);
    if (failedRefresh) toast.error('Dates moved, but the dashboard could not be refreshed for every date.');
    await loadImports(currentOrganization.id, selectedLocationId, false);
    await loadImportData(currentOrganization.id, selectedLocationId, selectedImportId);
  };

  const selectedLocation = locations.find(loc => loc.id.toString() === selectedLocationId);
  const selectedImport = imports.find(imp => imp.id.toString() === selectedImportId);
  const importDates = distinctDates(revenueData, roomStatsData);


  return (
    <>
      {/* PDF Loading Overlay */}
      {isLoadingPdf && (
        <div className="fixed inset-0 bg-black/75 z-[9999] flex justify-center items-center">
          <div className="bg-white p-6 rounded-lg shadow-xl">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto"></div>
            <p className="mt-4 text-center text-gray-600">Loading PDF...</p>
          </div>
        </div>
      )}

      {/* PDF Error Display */}
      {pdfError && (
        <div className="fixed inset-x-0 top-4 flex justify-center z-[9999]">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <span className="block sm:inline">{pdfError}</span>
            <button 
              className="absolute top-0 bottom-0 right-0 px-4 py-3"
              onClick={() => setPdfError(null)}
            >
              <span className="sr-only">Close</span>
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* PDF Modal */}
      {pdfUrl && (
        <FileViewerModal 
          url={pdfUrl} 
          fileType="pdf"
          onClose={() => {
            setPdfUrl(null);
            // Clean up the blob URL when closing the modal
            if (pdfUrl.startsWith('blob:')) {
              URL.revokeObjectURL(pdfUrl);
            }
          }} 
        />
      )}

      {isFileViewerOpen && fileUrl && (
        <FileViewerModal 
          url={fileUrl} 
          fileType={fileType}
          fileName={fileName}
          onClose={closeFileViewer} 
        />
      )}

      <div className={embedded ? '' : 'min-h-screen py-4 sm:py-8 pb-24 sm:pb-8'}>
        <div className={embedded ? '' : 'max-w-7xl mx-auto px-3 sm:px-6 lg:px-8'}>
        {!embedded && (
          <div className="mb-4 sm:mb-8">
            <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Import Review</h1>
            <p className="mt-1 text-sm text-gray-600 hidden sm:block">
              Review and edit imported hotel revenue and room statistics data
            </p>
          </div>
        )}

        {/* Filters Section */}
        <div className="bg-white shadow rounded-lg mb-4 sm:mb-6">
          <div
            className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 cursor-pointer flex items-center justify-between"
            onClick={() => toggleSection('filters')}
          >
            <div className="flex items-center">
              <Filter className="h-5 w-5 text-gray-400 mr-2" />
              <h2 className="text-base sm:text-lg font-medium text-gray-900">Filters</h2>
            </div>
            {expandedSections.filters ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
          </div>

          {expandedSections.filters && (
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {/* Organization Selector */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Organization
                  </label>
                  <select
                    value={currentOrganization?.id || ''}
                    onChange={(e) => handleOrganizationChange(e.target.value)}
                    className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="">Select Organization</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>

                {/* Location Selector */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Location
                  </label>
                  <select
                    value={selectedLocationId}
                    onChange={(e) => handleLocationChange(e.target.value)}
                    disabled={!currentOrganization}
                    className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 text-sm"
                  >
                    <option value="">Select Location</option>
                    {locations.map(location => (
                      <option key={location.id} value={location.id}>{location.name}</option>
                    ))}
                  </select>
                </div>

                {/* Import Selector with Navigation */}
                <div className="sm:col-span-2 lg:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Import
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigateImports('prev')}
                      disabled={currentImportIndex <= 0 || imports.length === 0}
                      className="px-3 py-2.5 sm:py-2 bg-gray-100 text-gray-700 rounded-md active:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </button>
                    <select
                      value={selectedImportId}
                      onChange={(e) => handleImportChange(e.target.value)}
                      disabled={!selectedLocationId || imports.length === 0}
                      className="flex-1 px-3 py-2.5 sm:py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 text-sm"
                    >
                      <option value="">Select Import</option>
                      {imports.map(imp => (
                        <option key={imp.id} value={imp.id}>
                          {formatDate(imp.effectiveDate || imp.createdAt)} - {imp.importFileType || 'Unknown'}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => navigateImports('next')}
                      disabled={currentImportIndex >= imports.length - 1 || imports.length === 0}
                      className="px-3 py-2.5 sm:py-2 bg-gray-100 text-gray-700 rounded-md active:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <ArrowRight className="h-4 w-4" />
                    </button>
                    {imports.length > 0 && (
                      <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">
                        {currentImportIndex + 1}/{imports.length}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Bulk Download Controls */}
              {selectedLocationId && imports.length > 0 && (
                <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Archive className="h-5 w-5 text-purple-600" />
                      <span className="font-medium text-purple-900 text-sm sm:text-base">Bulk Download</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-3 flex-1">
                      <div>
                        <label className="block text-xs text-purple-700 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={bulkDownloadStartDate}
                          onChange={(e) => setBulkDownloadStartDate(e.target.value)}
                          className="px-2 py-1.5 border border-purple-300 rounded text-sm w-full sm:w-auto"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-purple-700 mb-1">End Date</label>
                        <input
                          type="date"
                          value={bulkDownloadEndDate}
                          onChange={(e) => setBulkDownloadEndDate(e.target.value)}
                          className="px-2 py-1.5 border border-purple-300 rounded text-sm w-full sm:w-auto"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleBulkDownloadPdfs}
                          disabled={isBulkDownloading || !bulkDownloadStartDate || !bulkDownloadEndDate || getBulkDownloadMatches().length === 0}
                          className="inline-flex items-center px-3 py-1.5 bg-purple-600 text-white text-sm rounded-md active:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isBulkDownloading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                          ) : (
                            <Download className="h-4 w-4 mr-2" />
                          )}
                          {isBulkDownloading ? 'Downloading...' : 'Download All'}
                        </button>
                        {bulkDownloadStartDate && bulkDownloadEndDate && (
                          <span className="text-sm text-purple-700 whitespace-nowrap">
                            {getBulkDownloadMatches().length} file{getBulkDownloadMatches().length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Business date — every row of the import moves together */}
              {selectedImportId && (revenueData.length > 0 || roomStatsData.length > 0) && (
                <>
                  <div className="ir-date-card">
                    <Calendar aria-hidden="true" />
                    <div className="ir-date-main">
                      <span className="ir-date-label">Business date</span>
                      <span className="ir-date-value">{describeDates(importDates)}</span>
                    </div>
                    <button type="button" className="ir-btn ir-btn-sm" onClick={() => setDateSheetOpen(true)} disabled={loading}>
                      Change
                    </button>
                  </div>
                  <BusinessDateSheet
                    open={dateSheetOpen}
                    onOpenChange={setDateSheetOpen}
                    dates={importDates}
                    counts={{ revenue: revenueData.length, roomStats: roomStatsData.length }}
                    subtitle={`${selectedLocation ? `${selectedLocation.name} · ` : ''}import #${selectedImportId}`}
                    onSubmit={handleMoveBusinessDate}
                  />
                </>
              )}

              {/* Selected Import Info */}
              {selectedImport && selectedLocation && (
                <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-blue-50 rounded-lg">
                  {/* Mobile: Stacked layout */}
                  <div className="flex flex-col gap-3">
                    {/* Location and Import Info */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                      <div className="flex items-center">
                        <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 mr-2" />
                        <span className="font-medium text-blue-900 text-sm sm:text-base">{selectedLocation.name}</span>
                      </div>
                      <div className="flex items-center">
                        <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 mr-2" />
                        <span className="text-blue-800 text-sm">
                          #{selectedImport.id} - {formatDate(selectedImport.effectiveDate || selectedImport.createdAt)}
                          <span className="hidden sm:inline">{selectedImport.importFileType && ` (${selectedImport.importFileType})`}</span>
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons - Grid on mobile */}
                    <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                      {/* Mark Reviewed Button - Show first and prominent on mobile if flag exists */}
                      {hasManualReviewFlag && (
                        <button
                          onClick={handleClearReviewFlag}
                          disabled={isClearingReview || loading}
                          className="col-span-2 sm:col-span-1 inline-flex items-center justify-center px-3 py-2.5 sm:py-2 border border-green-300 rounded-md text-sm font-medium text-white bg-green-600 active:bg-green-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isClearingReview ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                          ) : (
                            <CheckSquare className="h-4 w-4 mr-2" />
                          )}
                          {isClearingReview ? 'Clearing...' : 'Mark Reviewed'}
                        </button>
                      )}

                      {/* View Original File Buttons */}
                      {selectedImport.nightAuditPath && (
                        <>
                          <button
                            onClick={handleViewFile}
                            disabled={isLoadingFile}
                            className="inline-flex items-center justify-center px-3 py-2.5 sm:py-2 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-white active:bg-blue-50 focus:outline-none disabled:opacity-50"
                          >
                            {isLoadingFile ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent mr-2"></div>
                            ) : (
                              <Eye className="h-4 w-4 mr-1 sm:mr-2" />
                            )}
                            <span className="hidden sm:inline">View </span>File
                          </button>

                          <button
                            onClick={handleDownloadFile}
                            className="inline-flex items-center justify-center px-3 py-2.5 sm:py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white active:bg-gray-50 focus:outline-none"
                          >
                            <Download className="h-4 w-4 mr-1 sm:mr-2" />
                            <span className="hidden sm:inline">Download</span>
                            <span className="sm:hidden">DL</span>
                          </button>
                        </>
                      )}

                      {/* Delete Import Button */}
                      <button
                        onClick={handleDeleteImport}
                        disabled={isDeletingImport || loading}
                        className="inline-flex items-center justify-center px-3 py-2.5 sm:py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white active:bg-red-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isDeletingImport ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-600 border-t-transparent mr-1 sm:mr-2"></div>
                        ) : (
                          <Trash2 className="h-4 w-4 mr-1 sm:mr-2" />
                        )}
                        <span className="hidden sm:inline">{isDeletingImport ? 'Deleting...' : 'Delete Import'}</span>
                        <span className="sm:hidden">{isDeletingImport ? '...' : 'Delete'}</span>
                      </button>
                    </div>
                  </div>

                  {/* File Path Info - Hidden on mobile */}
                  {selectedImport.nightAuditPath && (
                    <div className="mt-2 text-xs text-gray-600 hidden sm:block">
                      <span className="font-medium">File:</span> <span className="text-gray-500 break-all">{selectedImport.nightAuditPath}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading import data...</span>
          </div>
        )}

        {/* No Imports Found */}
        {selectedLocationId && !loading && imports.length === 0 && (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Imports Found</h3>
              <p className="text-gray-600 mb-4">
                No imports with active data records were found for this location.
              </p>
              <p className="cp-help">
                Imports only appear here if they still have corresponding records in the revenue or room statistics tables.
              </p>
            </div>
          </div>
        )}

        {/* Data Tabs */}
        {selectedImportId && !loading && (
          <div className="bg-white shadow rounded-lg">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex">
                <button
                  onClick={() => setActiveTab('revenue')}
                  className={`flex-1 sm:flex-none py-3 sm:py-4 px-4 sm:px-6 border-b-2 font-medium text-sm ${
                    activeTab === 'revenue'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 active:text-gray-700'
                  }`}
                >
                  Revenue ({revenueData.length})
                </button>
                <button
                  onClick={() => setActiveTab('roomStats')}
                  className={`flex-1 sm:flex-none py-3 sm:py-4 px-4 sm:px-6 border-b-2 font-medium text-sm ${
                    activeTab === 'roomStats'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 active:text-gray-700'
                  }`}
                >
                  Room Stats ({roomStatsData.length})
                </button>
              </nav>
            </div>

            {/* Reason for the correction being edited — logged with the save */}
            {(editingRevenue || editingRoomStats || isAddingRevenue || isAddingRoomStat) && (
              <div className="px-3 sm:px-6 pt-3 sm:pt-4">
                <label className="ir-note">
                  <span>Reason for this correction <span className="ir-help">(optional — kept on the file's history)</span></span>
                  <input
                    className="ir-input"
                    type="text"
                    maxLength={500}
                    value={correctionNote}
                    onChange={(e) => setCorrectionNote(e.target.value)}
                    placeholder="e.g. OCR misread 4,135 as 4,315"
                  />
                </label>
              </div>
            )}

            {/* Revenue Data Tab */}
            {activeTab === 'revenue' && (
              <div className="p-3 sm:p-6">
                {/* Add New Revenue Button */}
                <div className="mb-3 sm:mb-4 flex justify-center sm:justify-end">
                  <button
                    onClick={handleAddRevenue}
                    disabled={isAddingRevenue || editingRevenue !== null}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 sm:py-2 bg-blue-600 text-white rounded-md active:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="mr-2">+</span>
                    Add Revenue Row
                  </button>
                </div>

                {revenueData.length === 0 && !isAddingRevenue ? (
                  <div className="text-center py-8 sm:py-12">
                    <FileText className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 text-sm sm:text-base">No revenue data found for this import</p>
                  </div>
                ) : (
                  <>
                    {/* Mobile Card View */}
                    <div className="block sm:hidden space-y-3">
                      {/* New Revenue Card */}
                      {isAddingRevenue && newRevenue && (
                        <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                          <div className="text-xs font-medium text-green-700 mb-3">NEW REVENUE</div>
                          <div className="space-y-3">
                            <div>
                              <label className="cp-label">Date</label>
                              <input
                                type="date"
                                value={newRevenue.effectiveDate}
                                onChange={(e) => updateNewRevenueField('effectiveDate', e.target.value)}
                                className="cp-input"
                              />
                            </div>
                            <div>
                              <label className="cp-label">Revenue Type</label>
                              <SearchableSelect
                                options={revenueTypes}
                                value={newRevenue.revenueTypeId}
                                onChange={(value) => updateNewRevenueField('revenueTypeId', value)}
                                placeholder="Select Revenue Type"
                                getOptionLabel={(type) => type.name || type.accountName}
                                getOptionValue={(type) => type.id}
                                emptyMessage="No revenue types found"
                              />
                            </div>
                            <div>
                              <label className="cp-label">Amount</label>
                              <input
                                type="number"
                                step="0.01"
                                value={newRevenue.currentValue || ''}
                                onChange={(e) => updateNewRevenueField('currentValue', parseFloat(e.target.value) || null)}
                                className="cp-input"
                                placeholder="0.00"
                              />
                            </div>
                            <div className="flex gap-2 pt-2">
                              <button
                                onClick={handleSaveNewRevenue}
                                className="flex-1 px-3 py-2.5 bg-green-600 text-white rounded-md text-sm font-medium"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelAddRevenue}
                                className="flex-1 px-3 py-2.5 bg-gray-300 text-gray-700 rounded-md text-sm font-medium"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Existing Revenue Cards */}
                      {revenueData.map((revenue) => (
                        <div
                          key={revenue.id}
                          className={`bg-white border rounded-lg p-4 ${
                            editingRevenue?.id === revenue.id ? 'border-blue-400 border-2' : 'border-gray-200'
                          }`}
                        >
                          {editingRevenue?.id === revenue.id ? (
                            /* Editing Mode */
                            <div className="space-y-3">
                              <div>
                                <label className="cp-label">Date</label>
                                <input
                                  type="date"
                                  value={formatDateForInput(editingRevenue.effectiveDate)}
                                  onChange={(e) => updateEditingField('effectiveDate', e.target.value)}
                                  className="cp-input"
                                />
                              </div>
                              <div>
                                <label className="cp-label">Revenue Type</label>
                                <SearchableSelect
                                  options={revenueTypes}
                                  value={editingRevenue.revenueTypeId}
                                  onChange={(value) => updateEditingField('revenueTypeId', value)}
                                  placeholder="Select Revenue Type"
                                  getOptionLabel={(type) => type.name || type.accountName}
                                  getOptionValue={(type) => type.id}
                                  emptyMessage="No revenue types found"
                                />
                              </div>
                              <div>
                                <label className="cp-label">Amount</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={editingRevenue.currentValue || ''}
                                  onChange={(e) => updateEditingField('currentValue', parseFloat(e.target.value) || null)}
                                  className="cp-input"
                                />
                              </div>
                              <div className="flex gap-2 pt-2">
                                <button
                                  onClick={handleSaveRevenue}
                                  className="flex-1 px-3 py-2.5 bg-green-600 text-white rounded-md text-sm font-medium"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="flex-1 px-3 py-2.5 bg-gray-300 text-gray-700 rounded-md text-sm font-medium"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            /* View Mode */
                            <>
                              <div className="flex justify-between items-start mb-2">
                                <div className="text-lg font-semibold text-gray-900">
                                  {formatCurrency(revenue.currentValue, revenue.currency)}
                                </div>
                                <div className="cp-help">
                                  {formatDate(revenue.effectiveDate)}
                                </div>
                              </div>
                              <div className="text-sm text-gray-600 mb-3">
                                {revenueTypes.find(t => t.id === revenue.revenueTypeId)?.name ||
                                 revenueTypes.find(t => t.id === revenue.revenueTypeId)?.accountName ||
                                 'Unknown Type'}
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditRevenue(revenue)}
                                  className="flex-1 px-3 py-2 border border-blue-300 text-blue-700 rounded-md text-sm font-medium"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteRevenue(revenue.id)}
                                  className="px-3 py-2 border border-red-300 text-red-700 rounded-md text-sm font-medium"
                                >
                                  Delete
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden sm:block overflow-auto max-h-[70vh]">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Revenue Type (from Import)
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Maps To
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Amount
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {/* New Revenue Row */}
                        {isAddingRevenue && newRevenue && (
                          <tr className="bg-green-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <input
                                type="date"
                                value={newRevenue.effectiveDate}
                                onChange={(e) => updateNewRevenueField('effectiveDate', e.target.value)}
                                className="cp-input"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <SearchableSelect
                                options={revenueTypes}
                                value={newRevenue.revenueTypeId}
                                onChange={(value) => updateNewRevenueField('revenueTypeId', value)}
                                placeholder="Select Revenue Type"
                                getOptionLabel={(type) => type.name || type.accountName}
                                getOptionValue={(type) => type.id}
                                emptyMessage="No revenue types found"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 min-w-[200px]">
                              <MappedTypeSelect
                                typeId={newRevenue.revenueTypeId}
                                types={revenueTypes}
                                setTypes={setRevenueTypes}
                                orgId={currentOrganization?.id}
                                kind="revenue"
                                rowActive
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <input
                                type="number"
                                step="0.01"
                                value={newRevenue.currentValue || ''}
                                onChange={(e) => updateNewRevenueField('currentValue', parseFloat(e.target.value) || null)}
                                className="cp-input"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex space-x-2">
                                <button
                                  onClick={handleSaveNewRevenue}
                                  className="text-green-600 hover:text-green-900 flex items-center"
                                >
                                  <Save className="h-4 w-4 mr-1" />
                                  Save
                                </button>
                                <button
                                  onClick={handleCancelAddRevenue}
                                  className="text-gray-600 hover:text-gray-900 flex items-center"
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Cancel
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}

                        {/* Existing Revenue Rows */}
                        {revenueData.map((revenue) => (
                          <tr key={revenue.id} className={editingRevenue?.id === revenue.id ? 'bg-blue-50' : ''}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {editingRevenue?.id === revenue.id ? (
                                <input
                                  type="date"
                                  value={formatDateForInput(editingRevenue.effectiveDate)}
                                  onChange={(e) => updateEditingField('effectiveDate', e.target.value)}
                                  className="cp-input"
                                />
                              ) : (
                                formatDate(revenue.effectiveDate)
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {editingRevenue?.id === revenue.id ? (
                                <SearchableSelect
                                  options={revenueTypes}
                                  value={editingRevenue.revenueTypeId}
                                  onChange={(value) => updateEditingField('revenueTypeId', value)}
                                  placeholder="Select Revenue Type"
                                  getOptionLabel={(type) => type.name || type.accountName}
                                  getOptionValue={(type) => type.id}
                                  emptyMessage="No revenue types found"
                                />
                              ) : (
                                <SearchableSelect
                                  options={revenueTypes}
                                  value={revenue.revenueTypeId}
                                  onChange={async (newTypeId) => {
                                    try {
                                      await updateHotelRevenue(revenue.id, { ...revenue, revenueTypeId: newTypeId });
                                      setRevenueData(prev => prev.map(item =>
                                        item.id === revenue.id ? { ...item, revenueTypeId: newTypeId } : item
                                      ));
                                      toast.success('Revenue type updated successfully');
                                      recordCorrection(revenue.effectiveDate);
                                    } catch (error) {
                                      console.error('Error updating revenue type:', error);
                                      toast.error('Failed to update revenue type');
                                    }
                                  }}
                                  placeholder="Select Revenue Type"
                                  getOptionLabel={(type) => type.name || type.accountName}
                                  getOptionValue={(type) => type.id}
                                  disabled={editingRevenue !== null}
                                  emptyMessage="No revenue types found"
                                />
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 min-w-[200px]">
                              <MappedTypeSelect
                                typeId={editingRevenue?.id === revenue.id ? editingRevenue.revenueTypeId : revenue.revenueTypeId}
                                types={revenueTypes}
                                setTypes={setRevenueTypes}
                                orgId={currentOrganization?.id}
                                kind="revenue"
                                rowActive
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {editingRevenue?.id === revenue.id ? (
                                <input
                                  type="number"
                                  step="0.01"
                                  value={editingRevenue.currentValue || ''}
                                  onChange={(e) => updateEditingField('currentValue', parseFloat(e.target.value) || null)}
                                  className="cp-input"
                                  placeholder="0.00"
                                />
                              ) : (
                                formatCurrency(revenue.currentValue, revenue.currency)
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {editingRevenue?.id === revenue.id ? (
                                <div className="flex space-x-2">
                                  <button
                                    onClick={handleSaveRevenue}
                                    className="text-green-600 hover:text-green-900 flex items-center"
                                  >
                                    <Save className="h-4 w-4 mr-1" />
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    className="text-gray-600 hover:text-gray-900 flex items-center"
                                  >
                                    <X className="h-4 w-4 mr-1" />
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleEditRevenue(revenue)}
                                    className="text-blue-600 hover:text-blue-900 flex items-center"
                                  >
                                    <Edit className="h-4 w-4 mr-1" />
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteRevenue(revenue.id)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Room Stats Data Tab */}
            {activeTab === 'roomStats' && (
              <div className="p-3 sm:p-6">
                {/* Add New Room Stat Button */}
                <div className="mb-3 sm:mb-4 flex justify-center sm:justify-end">
                  <button
                    onClick={handleAddRoomStat}
                    disabled={isAddingRoomStat || editingRoomStats !== null}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 sm:py-2 bg-blue-600 text-white rounded-md active:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="mr-2">+</span>
                    Add Room Stat Row
                  </button>
                </div>

                {roomStatsData.length === 0 && !isAddingRoomStat ? (
                  <div className="text-center py-8 sm:py-12">
                    <FileText className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 text-sm sm:text-base">No room statistics data found for this import</p>
                  </div>
                ) : (
                  <>
                    {/* Mobile Card View for Room Stats */}
                    <div className="block sm:hidden space-y-3">
                      {/* New Room Stat Card */}
                      {isAddingRoomStat && newRoomStat && (
                        <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                          <div className="text-xs font-medium text-green-700 mb-3">NEW ROOM STAT</div>
                          <div className="space-y-3">
                            <div>
                              <label className="cp-label">Date</label>
                              <input
                                type="date"
                                value={newRoomStat.effectiveDate}
                                onChange={(e) => updateNewRoomStatField('effectiveDate', e.target.value)}
                                className="cp-input"
                              />
                            </div>
                            <div>
                              <label className="cp-label">Room Stat Type</label>
                              <select
                                value={newRoomStat.roomStatTypeId || ''}
                                onChange={(e) => updateNewRoomStatField('roomStatTypeId', parseInt(e.target.value) || null)}
                                className="cp-input"
                              >
                                <option value="">Select Type</option>
                                {roomStatTypes.map((type) => (
                                  <option key={type.id} value={type.id}>{type.name}</option>
                                ))}
                              </select>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <label className="cp-label">Current</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={newRoomStat.currentValue || ''}
                                  onChange={(e) => updateNewRoomStatField('currentValue', parseFloat(e.target.value) || null)}
                                  className="cp-input"
                                  placeholder="0"
                                />
                              </div>
                              <div>
                                <label className="cp-label">MTD</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={newRoomStat.mtdValue || ''}
                                  onChange={(e) => updateNewRoomStatField('mtdValue', parseFloat(e.target.value) || null)}
                                  className="cp-input"
                                  placeholder="0"
                                />
                              </div>
                              <div>
                                <label className="cp-label">YTD</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={newRoomStat.ytdValue || ''}
                                  onChange={(e) => updateNewRoomStatField('ytdValue', parseFloat(e.target.value) || null)}
                                  className="cp-input"
                                  placeholder="0"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2 pt-2">
                              <button
                                onClick={handleSaveNewRoomStat}
                                className="flex-1 px-3 py-2.5 bg-green-600 text-white rounded-md text-sm font-medium"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelAddRoomStat}
                                className="flex-1 px-3 py-2.5 bg-gray-300 text-gray-700 rounded-md text-sm font-medium"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Existing Room Stats Cards */}
                      {roomStatsData.map((stat) => (
                        <div
                          key={stat.id}
                          className={`bg-white border rounded-lg p-4 ${
                            editingRoomStats?.id === stat.id ? 'border-blue-400 border-2' : 'border-gray-200'
                          }`}
                        >
                          {editingRoomStats?.id === stat.id ? (
                            /* Editing Mode */
                            <div className="space-y-3">
                              <div>
                                <label className="cp-label">Date</label>
                                <input
                                  type="date"
                                  value={formatDateForInput(editingRoomStats.effectiveDate)}
                                  onChange={(e) => updateEditingField('effectiveDate', e.target.value, true)}
                                  className="cp-input"
                                />
                              </div>
                              <div>
                                <label className="cp-label">Room Stat Type</label>
                                <select
                                  value={editingRoomStats.roomStatTypeId || ''}
                                  onChange={(e) => updateEditingField('roomStatTypeId', parseInt(e.target.value) || null, true)}
                                  className="cp-input"
                                >
                                  <option value="">Select Type</option>
                                  {roomStatTypes.map((type) => (
                                    <option key={type.id} value={type.id}>{type.name}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="grid grid-cols-3 gap-2">
                                <div>
                                  <label className="cp-label">Current</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={editingRoomStats.currentValue || ''}
                                    onChange={(e) => updateEditingField('currentValue', parseFloat(e.target.value) || null, true)}
                                    className="cp-input"
                                  />
                                </div>
                                <div>
                                  <label className="cp-label">MTD</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={editingRoomStats.mtd || ''}
                                    onChange={(e) => updateEditingField('mtd', parseFloat(e.target.value) || null, true)}
                                    className="cp-input"
                                  />
                                </div>
                                <div>
                                  <label className="cp-label">YTD</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={editingRoomStats.ytd || ''}
                                    onChange={(e) => updateEditingField('ytd', parseFloat(e.target.value) || null, true)}
                                    className="cp-input"
                                  />
                                </div>
                              </div>
                              <div className="flex gap-2 pt-2">
                                <button
                                  onClick={handleSaveRoomStats}
                                  className="flex-1 px-3 py-2.5 bg-green-600 text-white rounded-md text-sm font-medium"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="flex-1 px-3 py-2.5 bg-gray-300 text-gray-700 rounded-md text-sm font-medium"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            /* View Mode */
                            <>
                              <div className="flex justify-between items-start mb-2">
                                <div className="cp-name">
                                  {roomStatTypes.find(t => t.id === stat.roomStatTypeId)?.name || 'Unknown Type'}
                                </div>
                                <div className="cp-help">
                                  {formatDate(stat.effectiveDate)}
                                </div>
                              </div>
                              <div className="grid grid-cols-3 gap-2 text-center mb-3">
                                <div>
                                  <div className="cp-help">Current</div>
                                  <div className="text-sm font-semibold">{stat.currentValue ?? '-'}</div>
                                </div>
                                <div>
                                  <div className="cp-help">MTD</div>
                                  <div className="text-sm font-semibold">{stat.mtdValue ?? '-'}</div>
                                </div>
                                <div>
                                  <div className="cp-help">YTD</div>
                                  <div className="text-sm font-semibold">{stat.ytdValue ?? '-'}</div>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditRoomStats(stat)}
                                  className="flex-1 px-3 py-2 border border-blue-300 text-blue-700 rounded-md text-sm font-medium"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteRoomStats(stat.id)}
                                  className="px-3 py-2 border border-red-300 text-red-700 rounded-md text-sm font-medium"
                                >
                                  Delete
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden sm:block overflow-auto max-h-[70vh]">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Room Stat Type (from Import)
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Maps To
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Current Value
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              MTD
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              YTD
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {/* New Room Stat Row */}
                        {isAddingRoomStat && newRoomStat && (
                          <tr className="bg-green-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <input
                                type="date"
                                value={newRoomStat.effectiveDate}
                                onChange={(e) => updateNewRoomStatField('effectiveDate', e.target.value)}
                                className="cp-input"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <select
                                value={newRoomStat.roomStatTypeId || ''}
                                onChange={(e) => updateNewRoomStatField('roomStatTypeId', parseInt(e.target.value) || null)}
                                className="cp-input"
                              >
                                <option value="">Select Room Stat Type</option>
                                {roomStatTypes.map((type) => (
                                  <option key={type.id} value={type.id}>
                                    {type.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 min-w-[200px]">
                              <MappedTypeSelect
                                typeId={newRoomStat.roomStatTypeId}
                                types={roomStatTypes}
                                setTypes={setRoomStatTypes}
                                orgId={currentOrganization?.id}
                                locationId={selectedLocationId ? parseInt(selectedLocationId) : null}
                                kind="roomStats"
                                rowActive
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <input
                                type="number"
                                step="0.01"
                                value={newRoomStat.currentValue || ''}
                                onChange={(e) => updateNewRoomStatField('currentValue', parseFloat(e.target.value) || null)}
                                className="cp-input"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <input
                                type="number"
                                step="0.01"
                                value={newRoomStat.mtd || ''}
                                onChange={(e) => updateNewRoomStatField('mtd', parseFloat(e.target.value) || null)}
                                className="border border-gray-300 rounded px-2 py-1 text-sm w-20"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <input
                                type="number"
                                step="0.01"
                                value={newRoomStat.ytd || ''}
                                onChange={(e) => updateNewRoomStatField('ytd', parseFloat(e.target.value) || null)}
                                className="border border-gray-300 rounded px-2 py-1 text-sm w-20"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex space-x-2">
                                <button
                                  onClick={handleSaveNewRoomStat}
                                  className="text-green-600 hover:text-green-900 flex items-center"
                                >
                                  <Save className="h-4 w-4 mr-1" />
                                  Save
                                </button>
                                <button
                                  onClick={handleCancelAddRoomStat}
                                  className="text-gray-600 hover:text-gray-900 flex items-center"
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Cancel
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}

                        {/* Existing Room Stat Rows */}
                        {roomStatsData.map((roomStat) => (
                          <tr key={roomStat.id} className={editingRoomStats?.id === roomStat.id ? 'bg-blue-50' : ''}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {editingRoomStats?.id === roomStat.id ? (
                                <input
                                  type="date"
                                  value={formatDateForInput(editingRoomStats.effectiveDate)}
                                  onChange={(e) => updateEditingField('effectiveDate', e.target.value, true)}
                                  className="cp-input"
                                />
                              ) : (
                                formatDate(roomStat.effectiveDate)
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {roomStat.roomStatTypeName || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 min-w-[200px]">
                              <MappedTypeSelect
                                typeId={roomStat.roomStatTypeId}
                                types={roomStatTypes}
                                setTypes={setRoomStatTypes}
                                orgId={currentOrganization?.id}
                                locationId={selectedLocationId ? parseInt(selectedLocationId) : null}
                                kind="roomStats"
                                rowActive
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {editingRoomStats?.id === roomStat.id ? (
                                <input
                                  type="number"
                                  step="0.01"
                                  value={editingRoomStats.currentValue || ''}
                                  onChange={(e) => updateEditingField('currentValue', parseFloat(e.target.value) || null, true)}
                                  className="cp-input"
                                  placeholder="0.00"
                                />
                              ) : (
                                roomStat.currentValue || 'N/A'
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {editingRoomStats?.id === roomStat.id ? (
                                <input
                                  type="number"
                                  step="0.01"
                                  value={editingRoomStats.mtd || ''}
                                  onChange={(e) => updateEditingField('mtd', parseFloat(e.target.value) || null, true)}
                                  className="border border-gray-300 rounded px-2 py-1 text-sm w-20"
                                />
                              ) : (
                                roomStat.mtd || '-'
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {editingRoomStats?.id === roomStat.id ? (
                                <input
                                  type="number"
                                  step="0.01"
                                  value={editingRoomStats.ytd || ''}
                                  onChange={(e) => updateEditingField('ytd', parseFloat(e.target.value) || null, true)}
                                  className="border border-gray-300 rounded px-2 py-1 text-sm w-20"
                                />
                              ) : (
                                roomStat.ytd || '-'
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {editingRoomStats?.id === roomStat.id ? (
                                <div className="flex space-x-2">
                                  <button
                                    onClick={handleSaveRoomStats}
                                    className="text-green-600 hover:text-green-900 flex items-center"
                                  >
                                    <Save className="h-4 w-4 mr-1" />
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    className="text-gray-600 hover:text-gray-900 flex items-center"
                                  >
                                    <X className="h-4 w-4 mr-1" />
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleEditRoomStats(roomStat)}
                                    className="text-blue-600 hover:text-blue-900 flex items-center"
                                  >
                                    <Edit className="h-4 w-4 mr-1" />
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteRoomStats(roomStat.id)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* No Data State */}
        {!selectedImportId && !loading && (
          <div className="text-center py-8 sm:py-12">
            <FileText className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No Import Selected</h3>
            <p className="text-gray-600 text-sm sm:text-base px-4">
              Select an organization, location, and import to review and edit the data.
            </p>
          </div>
        )}
        </div>
      </div>
    </>
  );
};

export default ImportReviewPage; 
